import React from 'react';
import { i18n } from 'config/locales';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import Suggestions from 'components/Form/Suggestions';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import SvgComponent from 'components/Form/SvgComponent';
import Textarea from 'components/Form/Textarea';
import { extractDate } from 'components/Section/History/dateranges';
import ValidationElement from '../ValidationElement';
import PVQName from '../PVQName';
import './Passport.scss';
import NotApplicable from '../NotApplicable';

export default class Passport extends ValidationElement {
  constructor(props) {
    super(props);

    this.formType = this.props.formType;
    this.openSuggestion = this.openSuggestion.bind(this);

    this.number = React.createRef();
    this.state = {
      showSuggestionsModal: false,
    };

    this.handleNameError = this.handleNameError.bind(this);
    this.openSuggestion = this.openSuggestion.bind(this);
  }

  handleNameError(value, arr) {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.indexOf('required') !== -1
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  }

  update = (queue, fn) => {
    const { requireCitizenshipUsPassportName, requireCitizenshipUsPassportExpiration } = this.props;
    this.props.onUpdate({
      Name: this.props.Name,
      Status: this.props.Status,
      Number: this.props.Number,
      Issued: this.props.Issued,
      Expiration: this.props.Expiration,
      PassportNumberNotApplicable: this.props.PassportNumberNotApplicable,
      PassportNumberNotApplicableExplanation: this.props.PassportNumberNotApplicableExplanation,
      suggestedNames: this.props.suggestedNames,
      requireCitizenshipUsPassportExpiration,
      requireCitizenshipUsPassportName,
      ...queue,
    });

    if (fn) {
      fn();
    }
  };

  updateName = (values) => {
    this.update({
      Name: values,
    });
  };

  updateStatus = (values) => {
    this.update({
      Status: values,
    });
  };

  updateNumber = (values) => {
    this.update({
      Number: values,
    });
  };

  updateIssued = (values) => {
    this.update({
      Issued: values,
    });
  };

  updateExpiration = (values) => {
    this.update({
      Expiration: values,
    });
  };

  updatePassportNumberNotApplicable = (values) => {
    const queue = { PassportNumberNotApplicable: values };

    if (values?.applicable) {
      queue.PassportNumberNotApplicableExplanation = {};
    } else {
      queue.Number = {};
    }

    this.update(queue);
  };

  updatePassportNumberNotApplicableExplanation = (values) => {
    this.update({
      PassportNumberNotApplicableExplanation: values,
    });
  };

  renderSuggestion = (suggestion = {}) => {
    const name = `${suggestion.first || ''} ${suggestion.middle || ''} ${suggestion.last || ''} ${suggestion.suffix || ''
    }`.trim();
    return <span>{name}</span>;
  };

  onSuggestion = (suggestion) => {
    this.update({
      Name: suggestion,
    });

    this.setState({ showSuggestionsModal: false });
  };

  onDismiss = () => {
    this.setState({ showSuggestionsModal: false });
  };

  openSuggestion() {
    this.setState({ showSuggestionsModal: true });
  }

  showSuggestions = () => {
    // If we have a name already, don't show
    if (this.props.Name && this.props.Name.first && this.props.Name.last) {
      return false;
    }

    // If we have suggestions, show them
    return !!this.props.suggestedNames.length;
  };

  passportBeforeCutoff = () => {
    if (this.props.Issued) {
      const cutoffDate = new Date('1/1/1990 00:00');
      const issueDate = extractDate(this.props.Issued);

      if (issueDate && issueDate < cutoffDate) {
        return true;
      }
    }

    return false;
  };

  render() {
    const { showSuggestionsModal } = this.state;
    const cutOff = this.passportBeforeCutoff();
    const numberLength = cutOff ? '255' : '9';
    const numberRegEx = cutOff ? '^[a-zA-Z0-9]*$' : this.props.reBook;
    const { suggestedNames } = this.props;

    let attribute = this.props.attribute;

    if (this.props.attribute === 'PassportBooks') { // PassportBooks is in an Accordion
      attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    }

    return (
      <div
        className="section-content passport"
      >
        <Field
          title={i18n.t(`citizenship.pvq.${this.props.passportType}.label.number`)}
          className="passport-question"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Number`}
        >
          <NotApplicable
            attribute={`${attribute}.PassportNumberNotApplicable`}
            exactAttribute
            {...this.props.PassportNumberNotApplicable}
            label={i18n.t(`citizenship.pvq.${this.props.passportType}.label.notApplicable`)}
            or={i18n.t(`citizenship.pvq.${this.props.passportType}.label.or`)}
            onUpdate={this.updatePassportNumberNotApplicable}
            includeNAConfirmation={Boolean(this.props.PassportNumber?.value)}
          >
            <Text
              attribute={`${attribute}.Number`}
              exactAttribute
              name={`${attribute}.Number`}
              {...this.props.Number}
              pattern={numberRegEx}
              maxlength={numberLength}
              className="number passport-number"
              ref={this.number}
              prefix="passport"
              onUpdate={this.updateNumber}
              onError={this.props.onError}
              required={this.props.required}
            />
          </NotApplicable>
        </Field>
        {this.props.PassportNumberNotApplicable?.applicable === false && (
          <Field
            title={i18n.t(`citizenship.pvq.${this.props.passportType}.label.explanation`)}
            titleSize="label"
            adjustFor="textarea"
            attribute={`${attribute}.PassportNumberNotApplicableExplanation`}
          >
            <Textarea
              attribute={`${attribute}.PassportNumberNotApplicableExplanation`}
              exactAttribute
              name="PassportNumberNotApplicableExplanation"
              className="passport-number-explanation"
              {...this.props.PassportNumberNotApplicableExplanation}
              onUpdate={this.updatePassportNumberNotApplicableExplanation}
              onError={this.props.onError}
            />
          </Field>
        )}
        {this.props.PassportNumberNotApplicable?.applicable !== false && (
          <>
            <div>
              <Field
                title={i18n.t(`citizenship.pvq.${this.props.passportType}.name`)}
                titleSize="h4"
                optional
                className="no-margin-bottom passport-question"
              />
              {this.showSuggestions() && (
              <div className="reuse-name">
                <button
                  type="button"
                  className="reuse-name-open-modal"
                  title={i18n.t(`citizenship.pvq.${this.props.passportType}.names.reuse`)}
                  onClick={this.openSuggestion}
                >
                  <div className="address-book" aria-hidden="true">
                    <SvgComponent className="address-book" src="/img/address-book.svg" />
                  </div>
                  <span>{i18n.t(`citizenship.pvq.${this.props.passportType}.names.reuse`)}</span>
                </button>
              </div>
              )}
              <Suggestions
                show={showSuggestionsModal}
                suggestions={suggestedNames}
                renderSuggestion={this.renderSuggestion}
                withSuggestions
                suggestionTitle={i18n.t('suggestions.name.title')}
                suggestionParagraph={i18n.t('suggestions.name.para')}
                suggestionLabel={i18n.t('suggestions.name.label')}
                suggestionDismissLabel={i18n.t('suggestions.name.dismiss')}
                suggestionUseLabel={i18n.t('suggestions.name.use')}
                onSuggestion={this.onSuggestion}
                onDismiss={this.onDismiss}
              />
              <Field
                optional
                filterErrors={Name.requiredErrorsOnly}
                scrollIntoView={this.props.scrollIntoView}
              >
                <PVQName
                  attribute={`${attribute}.Name`}
                  exactAttribute
                  aria-label="Provide full name"
                  name="name"
                  {...this.props.Name}
                  onUpdate={this.updateName}
                  onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  showLastFirstMiddleNameOrder
                />
              </Field>
            </div>
            <Field
              className="passport-question"
              title={i18n.t(`citizenship.pvq.${this.props.passportType}.issued`)}
              help={i18n.t(`citizenship.pvq.${this.props.passportType}.help.issued.message`)}
              helpTitle={i18n.t(`citizenship.pvq.${this.props.passportType}.help.issued.title`)}
              helpMessage={i18n.t(`citizenship.pvq.${this.props.passportType}.help.issued.message`)}
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Issued`}
            >
              <DateControl
                attribute={`${attribute}.Issued`}
                exactAttribute
                name="issued"
                className="passport-issued"
                {...this.props.Issued}
                minDateEqualTo
                onUpdate={this.updateIssued}
                onError={this.props.onError}
                required={this.props.required}
                showEstimated={false}
              />
            </Field>
            <Field
              className="passport-question"
              title={i18n.t(`citizenship.pvq.${this.props.passportType}.expiration`)}
              help={i18n.t(`citizenship.pvq.${this.props.passportType}.help.expiration.message`)}
              helpTitle={i18n.t(`citizenship.pvq.${this.props.passportType}.help.expiration.title`)}
              helpMessage={i18n.t(`citizenship.pvq.${this.props.passportType}.help.expiration.message`)}
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Expiration`}
            >
              <DateControl
                attribute={`${attribute}.Expiration`}
                exactAttribute
                name="expiration"
                className="passport-expiration"
                {...this.props.Expiration}
                minDate={this.props.Issued}
                minDateEqualTo
                prefix="passportInformation"
                onUpdate={this.updateExpiration}
                onError={this.props.onError}
                required={this.props.required}
                showEstimated={false}
              />
            </Field>

            <Branch
              attribute={`${attribute}.Status`}
              exactAttribute
              className="passport-question"
              name="passportstatus"
              label={i18n.t(`citizenship.pvq.${this.props.passportType}.status.title`)}
              labelSize="h4"
              {...this.props.Status}
              warning
              onUpdate={this.updateStatus}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </>
        )}
      </div>
    );
  }
}

// Regular expressions were based on the information provided by
// U.S. Citizenship and Immigration Services (USCIS) at:
//
// https://e-verify-uscis.gov/esp/help/EvHelpPassportandPassportCardNbr.htm
Passport.defaultProps = {
  Name: {},
  Status: {},
  Number: {},
  Issued: {},
  Expiration: {},
  PassportNumberNotApplicable: {
    applicable: true,
  },
  PassportNumberNotApplicableExplanation: {},
  attribute: 'Passport',
  passportType: '',
  applicantName: {},
  suggestedNames: [],
  reBook: '^[a-zA-Z0-9]{9}$',
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  errors: [],
};
